// The current user's status from users/user_status, shared across the navbar and
// the dashboard so one request feeds both.
//
// The endpoint carries two unrelated things behind one payload:
//   - the running task timer (task_id / type_id / start_time / elapsed_seconds),
//     all null when no timer runs;
//   - the work-time article pick (time_resource_id / time_resource_updated_on),
//     which is independent of the timer and must be readable while idle.
//
// Because of the second one, `data` is now always an object — it is no longer
// null when nothing is running. "Is a timer running" must therefore be a
// task_id test, which is what `activeTask` below exists to centralise.
//
// Memory-only, like useTextTemplates: it is per-session state that would be
// wrong the moment another user logs in, and logout clears it explicitly.

import { ref, computed } from 'vue';
import { format } from 'date-fns';
import axios from '@/services/axios';

const status = ref(null);
// Date.now() of the last successful fetch, so callers can anchor the server's
// elapsed_seconds to the local wall clock and keep ticking between polls.
const receivedAt = ref(0);
let statusPromise = null;

const articles = ref([]);
let articlesLoaded = false;
let articlesPromise = null;

// True when the pick is a non-default article last set before today, i.e. the
// user should be asked whether they are still on the right work-time type.
const stalePickPending = ref(false);

// Null when no timer runs. The four timer fields come back as null in that
// case, so the object itself says nothing — only task_id does.
export const activeTask = computed(() => (status.value?.task_id ? status.value : null));

export const timeResourceId = computed(() => status.value?.time_resource_id ?? null);

export const defaultArticle = computed(() => articles.value.find(a => a.is_default) || null);

export const currentArticle = computed(
    () => articles.value.find(a => a.id === timeResourceId.value) || null
);

// Drives the darker navbar clock: the light one means "default, nothing to see".
export const isNonDefaultPick = computed(
    () => timeResourceId.value != null && timeResourceId.value !== defaultArticle.value?.id
);

export { status, receivedAt, articles, stalePickPending };

// Needs both the status and the article list (to know which id is the default),
// and the two arrive independently — so both loaders call this and whichever
// lands first is a no-op.
function evaluateStalePick() {
    if (!status.value || !articles.value.length) return;

    const id = status.value.time_resource_id;
    // Unset, or on the default article: nothing to ask when the day changes.
    if (id == null || id === defaultArticle.value?.id) {
        stalePickPending.value = false;
        return;
    }

    // "Y-m-d H:i:s" in server-local time. Compare the date part as a string —
    // new Date("Y-m-d H:i:s") is not portable across engines.
    const updated = status.value.time_resource_updated_on;
    stalePickPending.value = !!updated && updated.slice(0, 10) < format(new Date(), 'yyyy-MM-dd');
}

// The navbar and the dashboard both ask on mount; concurrent callers share the
// one request, as loadArticles does.
export function fetchUserStatus() {
    if (statusPromise) return statusPromise;

    statusPromise = axios
        .get('users/user_status')
        .then(response => {
            status.value = response.data.data;
            receivedAt.value = Date.now();
            evaluateStalePick();
            return status.value;
        })
        .catch(error => {
            console.error('Error fetching user status:', error);
            return status.value;
        })
        .finally(() => {
            statusPromise = null;
        });
    return statusPromise;
}

// The four selectable work-time articles, in the order the server returns them
// (picker order, not alphabetical). Ids live on the server only — never
// hardcode them here.
export function loadArticles(force = false) {
    if (!force && (articlesLoaded || articlesPromise)) {
        // Concurrent callers share the in-flight request, as in useTextTemplates.
        return articlesPromise || Promise.resolve(articles.value);
    }
    articlesPromise = axios
        .get('/resources', { params: { is_material: 0, not_active: 0, active_time: 1 } })
        .then(response => {
            articles.value = response.data.data || [];
            articlesLoaded = true;
            evaluateStalePick();
            return articles.value;
        })
        .catch(error => {
            // Never let this block the navbar: an empty list just leaves the
            // clock light and skips the day-change question.
            console.error('Error fetching work time articles:', error);
            return articles.value;
        })
        .finally(() => {
            articlesPromise = null;
        });
    return articlesPromise;
}

// Pass null to clear. The key must always be present in the body — omitting it
// is a 400, not a no-op — and the timestamp is set server-side on every write,
// including a clear, so we never send one.
export async function setTimeResource(id) {
    const response = await axios.put('users/user_status', { time_resource_id: id ?? null });
    const payload = response.data.data;

    if (status.value) {
        status.value.time_resource_id = payload.time_resource_id;
        status.value.time_resource_updated_on = payload.time_resource_updated_on;
    } else {
        status.value = payload;
    }
    // Just written, so by definition no longer stale.
    stalePickPending.value = false;

    return payload;
}

export function clearUserStatus() {
    status.value = null;
    receivedAt.value = 0;
    articles.value = [];
    articlesLoaded = false;
    articlesPromise = null;
    statusPromise = null;
    stalePickPending.value = false;
}
